import { Children, forwardRef, ReactElement, ReactNode } from 'react';
import { styled } from '@mui/material/styles';

import MuiTabs, { TabsProps as MuiTabsProps, tabsClasses, TabsPropsIndicatorColorOverrides } from '@mui/material/Tabs';
import Tab from '../Tab';
import { shouldForwardProp as sysShouldForwardProp } from '@mui/system/createStyled';
import { OverridableStringUnion } from '@mui/types';

declare module '@mui/material/Tabs' {
  export interface TabsPropsIndicatorColorOverrides {
    internal: true;
  }
}

export * from '@mui/material/Tabs';

export type HeadlinesType = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

export interface TabsProps extends Omit<MuiTabsProps, 'indicatorColor'> {
  indicatorColor?: OverridableStringUnion<'primary' | 'internal', TabsPropsIndicatorColorOverrides>;
  withoutIndicator?: boolean;
  textVariant?: HeadlinesType;
  iconPosition?: 'start' | 'end' | 'bottom' | 'top';
}

const StyledTabs = styled(MuiTabs, {
  name: 'O2Tabs',
  slot: 'Root',
  shouldForwardProp: (propName) => sysShouldForwardProp(propName) && propName !== 'withoutIndicator',
})<TabsProps>(({ withoutIndicator }) => ({
  ...(withoutIndicator && {
    [`& .${tabsClasses.indicator}`]: {
      display: 'none',
    },
  }),
}));

const Tabs = forwardRef<HTMLDivElement, TabsProps>(
  ({ children, textVariant, iconPosition = 'start', ...props }, ref) => {
    return (
      <StyledTabs {...props} ref={ref}>
        {Children.map(children, (child: ReactNode) => {
          if (!child) return null;

          if (typeof child !== 'object') return child;

          return (
            <Tab
              textVariant={textVariant}
              key={(child as ReactElement).key}
              iconPosition={iconPosition}
              {...(child as ReactElement).props}
            />
          );
        })}
      </StyledTabs>
    );
  }
);

Tabs.displayName = 'Tabs';

export default Tabs;
